import React, { useEffect, useState, useCallback } from 'react';
import { useI18n } from '../context/i18n';
import './EmailBranding.css';

const API_BASE_URL = 'https://dashboard-dot-primeval-gear-417120.ue.r.appspot.com';

// BUG REAL (encontrado 2026-07-27, reportado por Alejo: "el logo no se ve en
// la pagina de la app"): esta pagina inventaba su propio company_id a partir
// del datasetId en localStorage ('tucketts', 'saldosahuevo', ...) -- un
// identificador que NO EXISTE en ningun lado del backend. El resto del app
// (Settings.jsx, etc.) usa el CompanyId REAL que ya viene resuelto en
// companyInfo (prop, desde App.js -> login) via c?.CompanyId || c?.company_id.
// Con el id inventado, cualquier "Guardar" quedaba escrito bajo una clave que
// el sync de Omnisend/Klaviyo (que sí usa el CompanyId real, ej. 'TUCK1')
// nunca iba a poder leer -> el logo jamas se veia en ningun lado, ni en el
// preview de esta pagina ni en los emails reales. Mismo bug transversal ya
// anotado en HANDOFF.md 2026-07-19 ("deuda de identidad de empresa en el
// frontend"): cada pagina no debe inventar su propio mapeo de company id.
const getCompanyId = (companyInfo) =>
  companyInfo?.CompanyId || companyInfo?.company_id || '';

const getAuthHeader = (token) => {
  const t = token || localStorage.getItem('token') || localStorage.getItem('idToken') || 'dashboard';
  return { Authorization: `Bearer ${t}` };
};

const DEFAULTS = {
  logo_url: '',
  button_color: '#111111',
  background_color: '#ffffff',
  text_color: '#111111',
  language: 'en',
};

const COPY = {
  es: {
    title: 'Brand Kit',
    subtitle: 'Estos colores, logo e idioma se usan para pintar los bloques de producto en tus campanas.',
    logo: 'Logo (URL)',
    logoPh: 'https://tutienda.com/logo.png',
    button: 'Color de boton',
    background: 'Color de fondo',
    text: 'Color de texto',
    language: 'Idioma del email',
    detect: 'Detectar desde mi tienda',
    detecting: 'Detectando...',
    save: 'Guardar',
    saving: 'Guardando...',
    saved: 'Guardado',
    preview: 'Vista previa',
    detectOk: 'Marca detectada. Revisa y guarda.',
    detectFail: 'No pudimos detectar la marca automaticamente. Ajustala a mano.',
    saveFail: 'No se pudo guardar. Intenta de nuevo.',
    dummy: 'Producto',
    cta: 'Comprar',
  },
  en: {
    title: 'Brand Kit',
    subtitle: 'These colors, logo and language are used to render product blocks in your campaigns.',
    logo: 'Logo (URL)',
    logoPh: 'https://yourstore.com/logo.png',
    button: 'Button color',
    background: 'Background color',
    text: 'Text color',
    language: 'Email language',
    detect: 'Detect from my store',
    detecting: 'Detecting...',
    save: 'Save',
    saving: 'Saving...',
    saved: 'Saved',
    preview: 'Preview',
    detectOk: 'Brand detected. Review and save.',
    detectFail: 'Could not auto-detect your brand. Adjust it manually.',
    saveFail: 'Could not save. Please try again.',
    dummy: 'Product',
    cta: 'Buy',
  },
};

const ColorField = ({ label, value, onChange }) => (
  <div className="eb-field">
    <label>{label}</label>
    <div className="eb-color-row">
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} />
      <input
        type="text"
        className="eb-hex-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
      />
    </div>
  </div>
);

const PreviewCard = ({ brand, ctaLabel, name }) => (
  <div className="eb-card" style={{ background: brand.background_color, color: brand.text_color }}>
    <div className="eb-card-img" />
    <div className="eb-card-title" style={{ color: brand.text_color }}>{name}</div>
    <div className="eb-card-price" style={{ color: brand.text_color }}>$29</div>
    <button className="eb-card-btn" style={{ background: brand.button_color }}>{ctaLabel}</button>
  </div>
);

const EmailBranding = ({ companyInfo, token }) => {
  const { language: uiLang } = useI18n();
  const c = COPY[uiLang === 'en' ? 'en' : 'es'];
  const companyId = getCompanyId(companyInfo);

  const [brand, setBrand] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState('');

  const set = (key) => (val) => setBrand((b) => ({ ...b, [key]: val }));

  // Detecta desde la tienda y aplica el resultado sobre el brand actual (sin
  // guardar solo). silent=true -> no pisa el mensaje de error/ok visible
  // (usado en el auto-detect de primera visita, para no alarmar si falla).
  // trigger 'auto' (default) | 'manual' -> se lo pasamos al backend, que
  // marca auto_detect_attempted siempre que corre, sin importar el trigger.
  const runDetect = useCallback(async ({ silent, trigger = 'auto' } = {}) => {
    setDetecting(true);
    if (!silent) setMsg('');
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/dashboard/brand-settings/detect?company=${encodeURIComponent(companyId)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeader(token) },
          body: JSON.stringify({ trigger }),
        }
      );
      const data = await res.json();
      if (res.ok && data.detected) {
        setBrand((b) => ({
          ...b,
          logo_url: data.logo_url || b.logo_url,
          button_color: data.button_color || b.button_color,
          background_color: data.background_color || b.background_color,
          text_color: data.text_color || b.text_color,
        }));
        setMsg(c.detectOk);
        setMsgType('ok');
      } else if (!silent) {
        setMsg(c.detectFail);
        setMsgType('warn');
      }
    } catch (_) {
      if (!silent) {
        setMsg(c.detectFail);
        setMsgType('warn');
      }
    } finally {
      setDetecting(false);
    }
  }, [companyId, token, c]);

  const load = useCallback(async () => {
    if (!companyId) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/dashboard/brand-settings?company=${encodeURIComponent(companyId)}`,
        { headers: getAuthHeader(token) }
      );
      if (res.ok) {
        const data = await res.json();
        setBrand({
          logo_url: data.logo_url || '',
          button_color: data.button_color || DEFAULTS.button_color,
          background_color: data.background_color || DEFAULTS.background_color,
          text_color: data.text_color || DEFAULTS.text_color,
          language: data.language === 'es' ? 'es' : 'en',
        });
        // Primera visita REAL del cliente (nunca guardó nada Y nunca se
        // intentó un auto-detect antes) -> detectar automático UNA sola vez
        // para que no vea la página vacía. auto_detect_attempted viene del
        // backend: se marca la primera vez que /detect corre (auto o
        // manual) y a partir de ahí el backend puede devolver exists=true
        // aunque el cliente nunca haya guardado nada él mismo (fila creada
        // con defaults al marcar el intento) -- por eso NO alcanza con
        // mirar exists solo, hay que chequear auto_detect_attempted para no
        // relanzar el auto-detect en cada visita. Si ya se intentó antes,
        // no se re-detecta solo -- el cliente decide cuándo con el botón
        // "Detect from my store" (decisión de Alejo 2026-07-27: simple, sin
        // sobre-ingeniería).
        if (!data.exists && !data.auto_detect_attempted) {
          runDetect({ silent: true, trigger: 'auto' });
        }
      }
    } catch (_) {
      // best-effort: dejamos defaults
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, token]);

  useEffect(() => { load(); }, [load]);

  const handleDetect = () => runDetect({ trigger: 'manual' });

  const handleSave = async () => {
    setSaving(true);
    setMsg('');
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/dashboard/brand-settings?company=${encodeURIComponent(companyId)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...getAuthHeader(token) },
          body: JSON.stringify(brand),
        }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setMsg(c.saved);
      setMsgType('ok');
    } catch (_) {
      setMsg(c.saveFail);
      setMsgType('warn');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="eb-wrap">
      <div className="eb-header">
        <h2>{c.title}</h2>
        <p>{c.subtitle}</p>
      </div>

      <div className="eb-grid">
        <div className="eb-form">
          <div className="eb-field">
            <label>{c.logo}</label>
            <input
              type="text"
              className="eb-text-input"
              placeholder={c.logoPh}
              value={brand.logo_url}
              onChange={(e) => set('logo_url')(e.target.value)}
              spellCheck={false}
            />
            {brand.logo_url ? (
              <img className="eb-logo-preview" src={brand.logo_url} alt="logo" onError={(e) => { e.target.style.display = 'none'; }} />
            ) : null}
          </div>

          <ColorField label={c.button} value={brand.button_color} onChange={set('button_color')} />
          <ColorField label={c.background} value={brand.background_color} onChange={set('background_color')} />
          <ColorField label={c.text} value={brand.text_color} onChange={set('text_color')} />

          <div className="eb-field">
            <label>{c.language}</label>
            <select value={brand.language} onChange={(e) => set('language')(e.target.value)}>
              <option value="en">English</option>
              <option value="es">Espanol</option>
            </select>
          </div>

          <div className="eb-actions">
            <button className="eb-btn-secondary" onClick={handleDetect} disabled={detecting || loading}>
              {detecting ? c.detecting : c.detect}
            </button>
            <button className="eb-btn-primary" onClick={handleSave} disabled={saving || loading}>
              {saving ? c.saving : c.save}
            </button>
          </div>

          {msg ? <div className={`eb-msg eb-msg-${msgType}`}>{msg}</div> : null}
        </div>

        <div className="eb-preview">
          <div className="eb-preview-label">{c.preview}</div>
          <div className="eb-preview-block" style={{ background: brand.background_color }}>
            {brand.logo_url ? (
              <img className="eb-preview-logo" src={brand.logo_url} alt="brand logo" onError={(e) => { e.target.style.display = 'none'; }} />
            ) : null}
            <div className="eb-cards">
              {[1, 2, 3].map((i) => (
                <PreviewCard key={i} brand={brand} ctaLabel={c.cta} name={`${c.dummy} ${i}`} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailBranding;
